import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DataTable, { DataTableRef } from 'components/tables/data-table';
import { useAuth } from 'contexts';
import { fetchTransactionsWithSpecifcation } from 'services';
import { downloadCsv } from 'services/download-csv';
import { Role } from 'types';
import { FilterOperator, FilterState, LogicalOperator } from 'utils';

import SuccessDialog from 'components/organization/dialog/success-dialog';
import useTimeZone from 'hooks/use-timezone';
import { defaultTransactionColumns, transactionDataColumns } from './transaction-columns';
import './transaction-list.scss';

const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { dateTimeLocalToUtc, newZonedDayJs, utcToZonedDateTimeLocal } = useTimeZone();
  const dataTableRef = useRef<DataTableRef>(null);

  const [deleted, setDeleted] = useState<false | undefined>(
    [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING].includes(userProfile.role) ? undefined : false
  );

  const initialColumns = useMemo(() => {
    return userProfile.columnPreferences.filter(cp => cp.table === 'TRANSACTION').length > 0
      ? userProfile.columnPreferences
          .filter(cp => cp.table === 'TRANSACTION')
          .map(({ column, width }) => {
            return { field: column, width };
          })
      : defaultTransactionColumns.map(c => {
          return { field: c, width: 80 };
        });
  }, [userProfile.columnPreferences]);

  const startDate: string = useMemo(() => {
    const encoded = searchParams.get('start');
    return encoded ? decodeURIComponent(encoded) : newZonedDayJs().subtract(4, 'week').startOf('day').toISOString();
  }, [newZonedDayJs, searchParams]);

  const endDate: string = useMemo(() => {
    const encoded = searchParams.get('end');
    return encoded ? decodeURIComponent(encoded) : newZonedDayJs().endOf('day').toISOString();
  }, [newZonedDayJs, searchParams]);

  const dateRange: FilterState = useMemo(() => {
    return {
      logicalOperator: LogicalOperator.AND,
      filters: [
        { field: 'completedDate', filterOperator: FilterOperator.ON_OR_AFTER, value: startDate },
        { field: 'completedDate', filterOperator: FilterOperator.ON_OR_BEFORE, value: endDate }
      ]
    };
  }, [startDate, endDate]);

  useEffect(() => {
    dataTableRef.current?.setFilterState(prev => {
      const filters = prev.filters || [];
      const otherFilters = filters.filter((f: any) => f.field !== 'completedDate');
      return {
        logicalOperator: prev.logicalOperator ?? LogicalOperator.AND,
        filters: [
          ...otherFilters,
          { field: 'completedDate', filterOperator: FilterOperator.ON_OR_AFTER, value: startDate },
          { field: 'completedDate', filterOperator: FilterOperator.ON_OR_BEFORE, value: endDate }
        ]
      };
    });
  }, [startDate, endDate]);
  const handlePresetApplied = (preset: any) => {
    if (preset) {
      const startFilter = preset.filters.find(
        (f: any) => f.field === 'completedDate' && f.filterOperator === FilterOperator.ON_OR_AFTER
      );
      const endFilter = preset.filters.find(
        (f: any) => f.field === 'completedDate' && f.filterOperator === FilterOperator.ON_OR_BEFORE
      );

      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          let changed = false;
          if (startFilter?.value && params.get('start') !== encodeURIComponent(startFilter.value)) {
            params.set('start', encodeURIComponent(startFilter.value));
            changed = true;
          }
          if (endFilter?.value && params.get('end') !== encodeURIComponent(endFilter.value)) {
            params.set('end', encodeURIComponent(endFilter.value));
            changed = true;
          }
          return changed ? params : prev;
        },
        { replace: true }
      );
    } else {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          let changed = false;
          if (params.has('start')) {
            params.delete('start');
            changed = true;
          }
          if (params.has('end')) {
            params.delete('end');
            changed = true;
          }
          return changed ? params : prev;
        },
        { replace: true }
      );
    }
  };

  return (
    <div className="page" id="transactions">
      <div className="page-header">
        <h1>TRANSACTIONS</h1>
        <div className="date-range d-flex flex-fill g-3 justify-content-center">
          <input
            type="datetime-local"
            value={utcToZonedDateTimeLocal(startDate)}
            onChange={e =>
              setSearchParams(
                prevState => {
                  const urlSearchParams = new URLSearchParams(prevState);
                  urlSearchParams.set('start', encodeURIComponent(dateTimeLocalToUtc(e.target.value)));
                  return urlSearchParams;
                },
                { replace: true }
              )
            }
          />
          <h1>-</h1>
          <input
            type="datetime-local"
            value={utcToZonedDateTimeLocal(endDate)}
            onChange={e =>
              setSearchParams(
                prevState => {
                  const urlSearchParams = new URLSearchParams(prevState);
                  urlSearchParams.set('end', encodeURIComponent(dateTimeLocalToUtc(e.target.value)));
                  return urlSearchParams;
                },
                { replace: true }
              )
            }
          />
          <button className="btn-back p-3 text-primary text-uppercase" onClick={() => setSearchParams({})}>
            reset filters
          </button>
        </div>
        {[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING].includes(userProfile.role) && (
          <div className="flex-fill d-flex justify-content-end g-3">
            <label className="text-info-dark">SHOW DELETED</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={deleted === undefined}
                onChange={e => setDeleted(e.target.checked ? undefined : false)}
              />
              <span className="slider round" />
            </label>
          </div>
        )}
      </div>
      <div className="flex-fill">
        <DataTable
          ref={dataTableRef}
          tableClass="t-height"
          fetchData={fetchTransactionsWithSpecifcation({ deleted })}
          fetchCsv={downloadCsv('transactions')}
          columns={transactionDataColumns}
          initialFilters={dateRange}
          initialPage={{ pageNum: 0, pageSize: 100, sortField: 'completedDate', sortDirection: 'desc' }}
          filterByLocation
          initialColumns={initialColumns}
          rowClassName={transaction => (!transaction?.deleted ? '' : 'deleted')}
          enableSaveColumns="TRANSACTION"
          showColumnsCategorized
          columnCategories={['Common', 'Container', 'Chassis', 'Genset']}
          onPresetApplied={handlePresetApplied}
        />
      </div>
      <SuccessDialog />
    </div>
  );
};

export default Transactions;
